import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X as XIcon, HelpCircle } from 'lucide-react';
import { FadeUp } from '../../../components/shared/Motion';
import { EditableText } from '../../components';

function FaqAccordion({ data, onChange }) {
  const update = (patch) => onChange && onChange({ ...data, ...patch });
  const items = data.items || [];
  const updateItem = (i, patch) => {
    const next = items.map((x, idx) => (idx === i ? { ...x, ...patch } : x));
    update({ items: next });
  };
  const [faqIdx, setFaqIdx] = useState(0);

  return (
    <section id="faq" className="relative py-24 lg:py-32 bg-white overflow-hidden">
      <div className="container-page">
        <FadeUp className="text-center max-w-3xl mx-auto mb-14">
          {data.eyebrow && (
            <span className="eyebrow justify-center"><span className="h-px w-8 bg-olive" />
              <EditableText value={data.eyebrow} onChange={(t) => update({ eyebrow: t })} as="span" />
            </span>
          )}
          <h2 className="section-title mt-4">
            {data.heading ? (
              <EditableText value={data.heading} onChange={(t) => update({ heading: t })} as="span" />
            ) : (
              <>
                <EditableText value={data.heading_prefix} onChange={(t) => update({ heading_prefix: t })} as="span" />
                <span className="text-olive"><EditableText value={data.heading_highlight} onChange={(t) => update({ heading_highlight: t })} as="span" /></span>
              </>
            )}
          </h2>
        </FadeUp>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mx-auto space-y-4"
        >
          {items.map((item, i) => {
            const active = faqIdx === i;
            return (
              <div
                key={i}
                className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${
                  active
                    ? 'border-olive/20 border-l-4 border-l-olive shadow-[0_20px_50px_-25px_rgba(46,58,24,0.35)]'
                    : 'border-olive/10 border-l-4 border-l-transparent hover:border-olive/25'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setFaqIdx(active ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                  aria-expanded={active}
                >
                  <div className="flex items-start gap-4">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-olive-light text-olive font-display font-bold text-sm">
                      <HelpCircle size={16} />
                    </span>
                    <EditableText
                      value={item.question}
                      onChange={(t) => updateItem(i, { question: t })}
                      as="h3"
                      className={`font-display text-lg lg:text-xl leading-snug ${active ? 'text-olive-deep font-bold' : 'text-ink/85 font-semibold'}`}
                    />
                  </div>
                  <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                    active ? 'bg-olive text-white rotate-0' : 'bg-olive-light text-olive'
                  }`}>
                    {active ? <XIcon size={18} /> : <Plus size={18} />}
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {active && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <EditableText
                        value={item.answer}
                        onChange={(t) => updateItem(i, { answer: t })}
                        as="div"
                        multiline
                        className="px-6 pb-6 pl-[4.5rem] text-ink/70 leading-relaxed"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

export default FaqAccordion;
